import React, {useEffect, useState} from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { Grid } from "@mui/material";

import {
  formatDate,
  MOMENT_FORMAT_DATE_DMYHM_DOTS,
} from "../../../functions/formatters";
import { resolutionCRUD} from "../../../http";
import {RESOLUTION_EDIT_ROUTE, RESOLUTION_ROUTE} from "../../../routes";
import CRUDTable from "../CRUDTable";
import { canDeleteDocumentRow, canShowDocumentDeleteAction } from '../../../functions/ksekDeletePermissions';
import { Button } from '@mui/material';
import { confirmDialog } from '../../Dialog/DialogDelete';

import { objectReferralFilterFields2, referralSearchFields } from "../../../sections/Filters/ReferralFilters";

import useAlert from "../../../hooks/useAlert";
import useReload from "../../../hooks/useReload";
import {REQUEST_STATE_ID_CREATED} from "../../../redux/constants/requestStateConstants";
import {requestStateIds} from "../../../models/requestStateIds";

export const ResolutionCRUDTable = ({ extraQuery: extraQueryProp, ...props }) => {
  const { t } = useTranslation();
  const { push } = useHistory();
  
  const [otherFilter, setOtherFilter] = useState({})

  useEffect(() => {
    if (props?.stateId) {
      setOtherFilter({ stateId: {operator: "equals", operand1: +props?.stateId} })
    }
  }, [props?.stateId])

  const canDelete = canShowDocumentDeleteAction();
  const canCreate = true;

  const alert = useAlert(false, 1);
  const { reload, reloadValue } = useReload();

  const itemActions = {
    title: "Действия",
    dataIndex: "actions",
    key: "actions",
    render: (_, row) => {
      return <Grid container spacing={1}>
        {canDeleteDocumentRow(row) && <Grid item xs={12}>
          <Button id="deleteReferal" color="warning" onClick={async () => {
            confirmDialog('Удаление элемента', 'Вы действительно хотите удалить этот элемент?', async () => {
              try {
                await resolutionCRUD.delete(row?.id)
                await reload();
              } catch (e) {
                console.log(e);
                alert.show(0, 'Не удалось удалить, ошибка ' + e);
              }
          })
          }}>Удалить</Button>
        </Grid>}
        {(row?.state?.code === REQUEST_STATE_ID_CREATED) && <Grid item xs={12}>
          <Button onClick={async () => {
            confirmDialog('Отправка в НЦЭ', 'Вы действительно хотите отправить эту заявку в НЦЭ?', async () => {
              try {
                await resolutionCRUD.workflowRun(row?.id, 1, 2)
                reload()
              } catch (e) {
                console.log(e);
                alert.show(0, 'Не удалось удалить, ошибка ' + e);
              }
            })
          }} id="deleteReferal">
              Отправить в НЦЭ
          </Button>
        </Grid>}
      </Grid>
    }
  }

  const columns = [
    { title: t("referralList.tableItem.id"), dataIndex: "id", key: "id" },
    {
      title: t("referralList.tableItem.number"),
      dataIndex: "number",
      key: "number",
    },

    {
      title: "Объект",
      dataIndex: "Объект",
      key: "Объект",
      render: (unknown, row) =>
        row.targetObjectId ? (
          row.targetObject.name
        ) : (
          <span style={{ color: "gray" }}>Не задан</span>
        ),
    },
    {
      title: "Филиал КСЭК",
      dataIndex: "sourceFilial",
      key: "sourceFilial",
      render: (unknown, row) =>
        row.filialId ? (
          row.sourceFilial?.name
        ) : (
          <span style={{ color: "gray" }}>Не задан</span>
        ),
    },
    {
      title: "Районное отделение (КСЭК)",
      dataIndex: "sourceFilialDivision",
      key: "sourceFilialDivision",
      render: (unknown, row) =>
        row.sourceFilialDivisionId ? (
          row.sourceFilialDivision?.name ||
          row.sourceFilialDivision?.Name ||
          `ID: ${row.sourceFilialDivisionId}`
        ) : (
          <span style={{ color: "gray" }}>Не задано</span>
        ),
    },
    {
      title: "Филиал НЦЭ",
      dataIndex: "filial",
      key: "filialNce",
      render: (unknown, row) =>
        row.filialId ? (
          row.filial?.name
        ) : (
          <span style={{ color: "gray" }}>Не задан</span>
        ),
    },
    {
      title: "Районное отделение (НЦЭ)",
      dataIndex: "filialDivision",
      key: "filialDivisionNce",
      render: (unknown, row) =>
        row.filialDivisionId ? (
          row.filialDivision?.name ||
          row.filialDivision?.Name ||
          `ID: ${row.filialDivisionId}`
        ) : (
          <span style={{ color: "gray" }}>Не задано</span>
        ),
    },
    {
      title: "Создано",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (unknown, row) => formatDate(row.createdAt, MOMENT_FORMAT_DATE_DMYHM_DOTS),
    },

    {
      title: "Статус",
      dataIndex: "state",
      key: "state",
      render: (unknown, row) =>
        row.state ? (
          row.state.name
        ) : (
          <span style={{ color: "gray" }}>Не задан</span>
        ),
    },
    {
      title: "Автор",
      dataIndex: "createdBy",
      key: "createdBy",
      render: (unknown, row) => row?.createdBy?.fullName || "-",
    },
    {...(canDelete && { ...itemActions })}
    ,
  ];

  const searchFields = [
    ...referralSearchFields
  ]

  const filterFields = [
    ...objectReferralFilterFields2
  ];

  const mergedExtraQuery = {
    ...(extraQueryProp || {}),
    includes: [
      ...new Set([
        "SourceFilialDivision",
        "FilialDivision",
        ...((extraQueryProp && extraQueryProp.includes) || []),
      ]),
    ],
  };

  return (
    <CRUDTable
      search={resolutionCRUD.search}
      columns={columns}
      onRowClick={({ id }) => push(RESOLUTION_EDIT_ROUTE(id))}
      onClickCreateButton={canCreate ? () => push(RESOLUTION_ROUTE("create")) : undefined}
      extraFilter={props?.stateId && otherFilter}
      filterFields={filterFields}
      searchFields={searchFields}
      reloadValue={props.reloadValue || reloadValue}
      rowClassNameHandler={(record) => {
        if (record.stateId === requestStateIds.declined) {
          return 'banned'
        }
      }}
      {...props}
      extraQuery={mergedExtraQuery}
    />
  );
};
